import { z } from "zod";

export namespace CommonModels {
/** 
 * ProficiencyEnumSchema 
 * @type { enum }
 */
export const ProficiencyEnumSchema = z.enum(["Not Applied", "Developing", "Foundational", "Intermediate", "Advanced", "Expert"]);
export type ProficiencyEnum = z.infer<typeof ProficiencyEnumSchema>;
export const ProficiencyEnum = ProficiencyEnumSchema.enum;

/** 
 * NameEnumSchema 
 * @type { enum }
 */
export const NameEnumSchema = z.enum(["linkedin", "instagram", "youtube", "facebook"]);
export type NameEnum = z.infer<typeof NameEnumSchema>;
export const NameEnum = NameEnumSchema.enum;

/** 
 * EmploymentTypeEnumSchema 
 * @type { enum }
 */
export const EmploymentTypeEnumSchema = z.enum(["Full-time", "Part-time", "Self-employed", "Freelance", "Contract", "Internship", "Apprenticeship", "Other"]);
export type EmploymentTypeEnum = z.infer<typeof EmploymentTypeEnumSchema>;
export const EmploymentTypeEnum = EmploymentTypeEnumSchema.enum;

/** 
 * AchievementCategoryEnumSchema 
 * @type { enum }
 */
export const AchievementCategoryEnumSchema = z.enum(["Academic", "Professional", "Competition", "Recognition", "Other"]);
export type AchievementCategoryEnum = z.infer<typeof AchievementCategoryEnumSchema>;
export const AchievementCategoryEnum = AchievementCategoryEnumSchema.enum;

/** 
 * OnboardingStepEnumSchema 
 * @type { enum }
 */
export const OnboardingStepEnumSchema = z.enum(["resume_upload", "personal_info", "work_experience", "education", "skills", "certifications", "completed"]);
export type OnboardingStepEnum = z.infer<typeof OnboardingStepEnumSchema>;
export const OnboardingStepEnum = OnboardingStepEnumSchema.enum;

/** 
 * WorkSettingEnumSchema 
 * @type { enum }
 */
export const WorkSettingEnumSchema = z.enum(["On-site", "Remote", "Hybrid", "Unknown"]);
export type WorkSettingEnum = z.infer<typeof WorkSettingEnumSchema>;
export const WorkSettingEnum = WorkSettingEnumSchema.enum;

/** 
 * CompanyIndustryEnumSchema 
 * @type { enum }
 */
export const CompanyIndustryEnumSchema = z.enum(["Corporate", "Startup", "Non-profit", "Government", "Academic", "Professional Services", "Other", "Unknown"]);
export type CompanyIndustryEnum = z.infer<typeof CompanyIndustryEnumSchema>;
export const CompanyIndustryEnum = CompanyIndustryEnumSchema.enum;

/** 
 * StatusEnumSchema 
 * @type { enum }
 */
export const StatusEnumSchema = z.enum(["no_data", "enqueued", "processing", "processing_full", "completed", "failed"]);
export type StatusEnum = z.infer<typeof StatusEnumSchema>;
export const StatusEnum = StatusEnumSchema.enum;

/** 
 * LiveShowCategoryEnumSchema 
 * @type { enum }
 */
export const LiveShowCategoryEnumSchema = z.enum(["FREE", "PREMIUM"]);
export type LiveShowCategoryEnum = z.infer<typeof LiveShowCategoryEnumSchema>;
export const LiveShowCategoryEnum = LiveShowCategoryEnumSchema.enum;

/** 
 * LiveShowStatusEnumSchema 
 * @type { enum }
 */
export const LiveShowStatusEnumSchema = z.enum(["SCHEDULED", "LIVE", "ENDED", "CANCELLED"]);
export type LiveShowStatusEnum = z.infer<typeof LiveShowStatusEnumSchema>;
export const LiveShowStatusEnum = LiveShowStatusEnumSchema.enum;

/** 
 * VideoTypeEnumSchema 
 * @type { enum }
 */
export const VideoTypeEnumSchema = z.enum(["live-stream", "uploaded-video"]);
export type VideoTypeEnum = z.infer<typeof VideoTypeEnumSchema>;
export const VideoTypeEnum = VideoTypeEnumSchema.enum;

/** 
 * PortfolioCategoryEnumSchema 
 * @type { enum }
 */
export const PortfolioCategoryEnumSchema = z.enum(["Stock", "Crypto", "Cash"]);
export type PortfolioCategoryEnum = z.infer<typeof PortfolioCategoryEnumSchema>;
export const PortfolioCategoryEnum = PortfolioCategoryEnumSchema.enum;

/** 
 * SecurityTypeEnumSchema 
 * @type { enum }
 */
export const SecurityTypeEnumSchema = z.enum(["equity", "etf", "cryptocurrency", "cash"]);
export type SecurityTypeEnum = z.infer<typeof SecurityTypeEnumSchema>;
export const SecurityTypeEnum = SecurityTypeEnumSchema.enum;

/** 
 * BizProfileListStatusEnumSchema 
 * @type { enum }
 */
export const BizProfileListStatusEnumSchema = z.enum(["initialized", "queued", "processing", "processed", "processing_failed"]);
export type BizProfileListStatusEnum = z.infer<typeof BizProfileListStatusEnumSchema>;
export const BizProfileListStatusEnum = BizProfileListStatusEnumSchema.enum;

/** 
 * EndorsementStatusEnumSchema 
 * @type { enum }
 * @description Current status of the endorsement. Example: `APPROVED`
 */
export const EndorsementStatusEnumSchema = z.enum(["REQUESTED", "PENDING_APPROVAL", "APPROVED", "REJECTED", "IGNORED"]);
export type EndorsementStatusEnum = z.infer<typeof EndorsementStatusEnumSchema>;
export const EndorsementStatusEnum = EndorsementStatusEnumSchema.enum;

/** 
 * SocialMediaDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } url  
 */
export const SocialMediaDtoSchema = z.object({ name: CommonModels.NameEnumSchema, url: z.string() });
export type SocialMediaDto = z.infer<typeof SocialMediaDtoSchema>;


/** 
 * ContactDtoSchema 
 * @type { object }
 * @property { string } email  
 * @property { string } phone  
 * @property { string } phoneCountry  
 * @property { SocialMediaDto[] } social_media  
 */
export const ContactDtoSchema = z.object({ email: z.string().nullable(), phone: z.string(), phoneCountry: z.string().nullish(), social_media: z.array(CommonModels.SocialMediaDtoSchema) });
export type ContactDto = z.infer<typeof ContactDtoSchema>;


/** 
 * SkillDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } proficiency  
 */
export const SkillDtoSchema = z.object({ name: z.string(), proficiency: CommonModels.ProficiencyEnumSchema.nullable() });
export type SkillDto = z.infer<typeof SkillDtoSchema>;


/** 
 * LocationDtoSchema 
 * @type { object }
 * @property { string } city  
 * @property { string } state  
 * @property { string } country  
 * @property { string } postal_code  
 * @property { string } raw_address  
 * @property { string } msa_code  
 * @property { string } msa_title  
 */
export const LocationDtoSchema = z.object({ city: z.string().nullable(), state: z.string().nullable(), country: z.string().nullish(), postal_code: z.string().nullish(), raw_address: z.string().nullish(), msa_code: z.string().nullish(), msa_title: z.string().nullish() });
export type LocationDto = z.infer<typeof LocationDtoSchema>;


/** 
 * PersonProfileDtoSchema 
 * @type { object }
 * @property { string } pronoun  
 * @property { SkillDto[] } skills  
 * @property { ContactDto } contact  
 * @property { LocationDto } location  
 */
export const PersonProfileDtoSchema = z.object({ pronoun: z.string().nullish(), skills: z.array(CommonModels.SkillDtoSchema), contact: CommonModels.ContactDtoSchema, location: CommonModels.LocationDtoSchema });
export type PersonProfileDto = z.infer<typeof PersonProfileDtoSchema>;


/** 
 * PersonMetadataDtoSchema 
 * @type { object }
 * @property { number } total_years_of_experience  
 */
export const PersonMetadataDtoSchema = z.object({ total_years_of_experience: z.number().nullable() }).partial();
export type PersonMetadataDto = z.infer<typeof PersonMetadataDtoSchema>;


/** 
 * PersonDtoSchema 
 * @type { object }
 * @property { PersonProfileDto } profile  
 * @property { PersonMetadataDto } metadata  
 */
export const PersonDtoSchema = z.object({ profile: CommonModels.PersonProfileDtoSchema, metadata: CommonModels.PersonMetadataDtoSchema.nullish() });
export type PersonDto = z.infer<typeof PersonDtoSchema>;


/** 
 * PeriodDtoSchema 
 * @type { object }
 * @property { string } start_date  
 * @property { string } end_date  
 */
export const PeriodDtoSchema = z.object({ start_date: z.string().nullable(), end_date: z.string().nullable() });
export type PeriodDto = z.infer<typeof PeriodDtoSchema>;


/** 
 * CompanyPositionDtoSchema 
 * @type { object }
 * @property { PeriodDto } period  
 * @property { LocationDto } location  
 * @property { string } work_setting  
 * @property { string } role_title  
 * @property { string } description  
 * @property { string } employment_type  
 * @property { number } salary  
 * @property { boolean } is_current  
 * @property { string } bls_code  
 * @property { string } bls_title  
 * @property { string } id  
 * @property { number } job_zone_id  
 */
export const CompanyPositionDtoSchema = z.object({ period: CommonModels.PeriodDtoSchema, location: CommonModels.LocationDtoSchema, work_setting: z.string().nullable(), role_title: z.string().nullable(), description: z.string().nullable(), employment_type: CommonModels.EmploymentTypeEnumSchema.nullable(), salary: z.number().nullable(), is_current: z.boolean(), bls_code: z.string().nullish(), bls_title: z.string().nullish(), id: z.string().nullish(), job_zone_id: z.number().nullish() });
export type CompanyPositionDto = z.infer<typeof CompanyPositionDtoSchema>;


/** 
 * CompanyDtoSchema 
 * @type { object }
 * @property { CompanyPositionDto[] } positions  
 * @property { string } name  
 * @property { string } industry  
 * @property { string } hash_id  
 */
export const CompanyDtoSchema = z.object({ positions: z.array(CommonModels.CompanyPositionDtoSchema), name: z.string().nullable(), industry: z.string().nullable(), hash_id: z.string().nullish() });
export type CompanyDto = z.infer<typeof CompanyDtoSchema>;


/** 
 * EducationDtoSchema 
 * @type { object }
 * @property { PeriodDto } period  
 * @property { string } degree  
 * @property { string } field_of_study  
 * @property { string } institution_name  
 * @property { LocationDto } location  
 * @property { string } hash_id  
 * @property { string } grade  
 * @property { string } description  
 */
export const EducationDtoSchema = z.object({ period: CommonModels.PeriodDtoSchema, degree: z.string().nullable(), field_of_study: z.string().nullable(), institution_name: z.string().nullable(), location: CommonModels.LocationDtoSchema, hash_id: z.string().nullish(), grade: z.string().nullish(), description: z.string().nullish() });
export type EducationDto = z.infer<typeof EducationDtoSchema>;


/** 
 * VolunteerExperienceDtoSchema 
 * @type { object }
 * @property { string } organization_name  
 * @property { PeriodDto } period  
 * @property { string } role  
 * @property { string } description  
 * @property { LocationDto } location  
 */
export const VolunteerExperienceDtoSchema = z.object({ organization_name: z.string().nullable(), period: CommonModels.PeriodDtoSchema.nullable(), role: z.string().nullable(), description: z.string().nullable(), location: CommonModels.LocationDtoSchema.nullable() });
export type VolunteerExperienceDto = z.infer<typeof VolunteerExperienceDtoSchema>;


/** 
 * AchievementOrAwardDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } date  
 * @property { string } category  
 * @property { string } description  
 * @property { string } issuer  
 */
export const AchievementOrAwardDtoSchema = z.object({ title: z.string(), date: z.string().nullable(), category: CommonModels.AchievementCategoryEnumSchema.nullable(), description: z.string().nullable(), issuer: z.string().nullable() });
export type AchievementOrAwardDto = z.infer<typeof AchievementOrAwardDtoSchema>;


/** 
 * LicenseOrCertificationDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } issuing_organization  
 * @property { string } issue_date  
 * @property { string } credential_id  
 * @property { string } verification_url  
 */
export const LicenseOrCertificationDtoSchema = z.object({ name: z.string(), issuing_organization: z.string().nullable(), issue_date: z.string().nullable(), credential_id: z.string().nullable(), verification_url: z.string().nullable() });
export type LicenseOrCertificationDto = z.infer<typeof LicenseOrCertificationDtoSchema>;


/** 
 * OthersDtoSchema 
 * @type { object }
 * @property { AchievementOrAwardDto[] } achievements_or_awards  
 * @property { LicenseOrCertificationDto[] } licenses_or_certifications  
 * @property { VolunteerExperienceDto[] } volunteer_experiences  
 */
export const OthersDtoSchema = z.object({ achievements_or_awards: z.array(CommonModels.AchievementOrAwardDtoSchema), licenses_or_certifications: z.array(CommonModels.LicenseOrCertificationDtoSchema), volunteer_experiences: z.array(CommonModels.VolunteerExperienceDtoSchema) });
export type OthersDto = z.infer<typeof OthersDtoSchema>;


/** 
 * ProfileV2DataDtoSchema 
 * @type { object }
 * @property { PersonDto } person  
 * @property { CompanyDto[] } companies  
 * @property { EducationDto[] } education  
 * @property { OthersDto } others  
 */
export const ProfileV2DataDtoSchema = z.object({ person: CommonModels.PersonDtoSchema, companies: z.array(CommonModels.CompanyDtoSchema), education: z.array(CommonModels.EducationDtoSchema), others: CommonModels.OthersDtoSchema });
export type ProfileV2DataDto = z.infer<typeof ProfileV2DataDtoSchema>;


/** 
 * ProfileStatusEnumSchema 
 * @type { enum }
 */
export const ProfileStatusEnumSchema = z.enum(["initialized", "onboarding", "queued", "processing", "processed", "processing_failed"]);
export type ProfileStatusEnum = z.infer<typeof ProfileStatusEnumSchema>;
export const ProfileStatusEnum = ProfileStatusEnumSchema.enum;

/** 
 * SocialMediaUpdateDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } url  
 */
export const SocialMediaUpdateDtoSchema = z.object({ name: CommonModels.NameEnumSchema, url: z.string() });
export type SocialMediaUpdateDto = z.infer<typeof SocialMediaUpdateDtoSchema>;


/** 
 * ContactUpdateDtoSchema 
 * @type { object }
 * @property { string } email  
 * @property { string } phone Min Length: `1` 
 * @property { string } phoneCountry Min Length: `2`. Max Length: `2` 
 * @property { SocialMediaUpdateDto[] } social_media  
 */
export const ContactUpdateDtoSchema = z.object({ email: z.string(), phone: z.string().min(1), phoneCountry: z.string().min(2).max(2).nullish(), social_media: z.array(CommonModels.SocialMediaUpdateDtoSchema) });
export type ContactUpdateDto = z.infer<typeof ContactUpdateDtoSchema>;


/** 
 * SkillUpdateDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } proficiency  
 */
export const SkillUpdateDtoSchema = z.object({ name: z.string(), proficiency: CommonModels.ProficiencyEnumSchema.nullable() });
export type SkillUpdateDto = z.infer<typeof SkillUpdateDtoSchema>;


/** 
 * LanguageUpdateDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } proficiency  
 */
export const LanguageUpdateDtoSchema = z.object({ name: z.string(), proficiency: z.string() });
export type LanguageUpdateDto = z.infer<typeof LanguageUpdateDtoSchema>;


/** 
 * PersonProfileLocationUpdateDtoSchema 
 * @type { object }
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country Min Length: `2`. Max Length: `2` 
 * @property { string } postal_code  
 */
export const PersonProfileLocationUpdateDtoSchema = z.object({ city: z.string(), state: z.string().max(2), country: z.string().min(2).max(2), postal_code: z.string().nullish() });
export type PersonProfileLocationUpdateDto = z.infer<typeof PersonProfileLocationUpdateDtoSchema>;


/** 
 * PersonProfileUpdateDtoSchema 
 * @type { object }
 * @property { string } pronoun  
 * @property { SkillUpdateDto[] } skills  
 * @property { LanguageUpdateDto[] } languages  
 * @property { ContactUpdateDto } contact  
 * @property { PersonProfileLocationUpdateDto } location  
 */
export const PersonProfileUpdateDtoSchema = z.object({ pronoun: z.string().nullish(), skills: z.array(CommonModels.SkillUpdateDtoSchema), languages: z.array(CommonModels.LanguageUpdateDtoSchema).nullish(), contact: CommonModels.ContactUpdateDtoSchema, location: CommonModels.PersonProfileLocationUpdateDtoSchema });
export type PersonProfileUpdateDto = z.infer<typeof PersonProfileUpdateDtoSchema>;


/** 
 * PersonUpdateDtoSchema 
 * @type { object }
 * @property { PersonProfileUpdateDto } profile  
 */
export const PersonUpdateDtoSchema = z.object({ profile: CommonModels.PersonProfileUpdateDtoSchema });
export type PersonUpdateDto = z.infer<typeof PersonUpdateDtoSchema>;


/** 
 * PeriodUpdateDtoSchema 
 * @type { object }
 * @property { string } start_date  
 * @property { string } end_date  
 */
export const PeriodUpdateDtoSchema = z.object({ start_date: z.string().nullable(), end_date: z.string().nullable() });
export type PeriodUpdateDto = z.infer<typeof PeriodUpdateDtoSchema>;


/** 
 * LocationUpdateDtoSchema 
 * @type { object }
 * @property { string } city  
 * @property { string } state Max Length: `2` 
 * @property { string } country  
 * @property { string } postal_code  
 */
export const LocationUpdateDtoSchema = z.object({ city: z.string(), state: z.string().max(2), country: z.string().nullish(), postal_code: z.string().nullish() });
export type LocationUpdateDto = z.infer<typeof LocationUpdateDtoSchema>;


/** 
 * CompanyPositionUpdateDtoSchema 
 * @type { object }
 * @property { PeriodUpdateDto } period  
 * @property { LocationUpdateDto } location  
 * @property { string } work_setting  
 * @property { string } role_title  
 * @property { string } description  
 * @property { string } employment_type  
 * @property { number } salary  
 * @property { boolean } is_current  
 * @property { string } id  
 */
export const CompanyPositionUpdateDtoSchema = z.object({ period: CommonModels.PeriodUpdateDtoSchema, location: CommonModels.LocationUpdateDtoSchema, work_setting: CommonModels.WorkSettingEnumSchema, role_title: z.string(), description: z.string(), employment_type: CommonModels.EmploymentTypeEnumSchema.nullable(), salary: z.number().nullable(), is_current: z.boolean(), id: z.string().nullish() });
export type CompanyPositionUpdateDto = z.infer<typeof CompanyPositionUpdateDtoSchema>;


/** 
 * CompanyUpdateDtoSchema 
 * @type { object }
 * @property { CompanyPositionUpdateDto[] } positions  
 * @property { string } name  
 * @property { string } industry  
 * @property { string } hash_id  
 */
export const CompanyUpdateDtoSchema = z.object({ positions: z.array(CommonModels.CompanyPositionUpdateDtoSchema), name: z.string(), industry: CommonModels.CompanyIndustryEnumSchema.nullable(), hash_id: z.string().nullish() });
export type CompanyUpdateDto = z.infer<typeof CompanyUpdateDtoSchema>;


/** 
 * EducationUpdateDtoSchema 
 * @type { object }
 * @property { PeriodUpdateDto } period  
 * @property { string } degree  
 * @property { string } field_of_study  
 * @property { string } institution_name  
 * @property { LocationUpdateDto } location  
 * @property { string } hash_id  
 * @property { string } grade  
 * @property { string } description  
 */
export const EducationUpdateDtoSchema = z.object({ period: CommonModels.PeriodUpdateDtoSchema, degree: z.string(), field_of_study: z.string().nullable(), institution_name: z.string(), location: CommonModels.LocationUpdateDtoSchema, hash_id: z.string().nullish(), grade: z.string().nullable(), description: z.string().nullable() });
export type EducationUpdateDto = z.infer<typeof EducationUpdateDtoSchema>;


/** 
 * VolunteerExperienceUpdateDtoSchema 
 * @type { object }
 * @property { string } organization_name  
 * @property { PeriodUpdateDto } period  
 * @property { string } role  
 * @property { string } description  
 * @property { LocationUpdateDto } location  
 */
export const VolunteerExperienceUpdateDtoSchema = z.object({ organization_name: z.string(), period: CommonModels.PeriodUpdateDtoSchema, role: z.string(), description: z.string().nullable(), location: CommonModels.LocationUpdateDtoSchema });
export type VolunteerExperienceUpdateDto = z.infer<typeof VolunteerExperienceUpdateDtoSchema>;


/** 
 * PublicationUpdateDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } publication_type  
 * @property { string[] } authors  
 * @property { string } publication_venue  
 * @property { string } date  
 * @property { string } doi  
 * @property { string } url  
 */
export const PublicationUpdateDtoSchema = z.object({ title: z.string(), publication_type: z.string(), authors: z.array(z.string()), publication_venue: z.string().nullable(), date: z.string().nullable(), doi: z.string().nullable(), url: z.string().nullable() });
export type PublicationUpdateDto = z.infer<typeof PublicationUpdateDtoSchema>;


/** 
 * AchievementOrAwardUpdateDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } date  
 * @property { string } category  
 * @property { string } description  
 * @property { string } issuer  
 */
export const AchievementOrAwardUpdateDtoSchema = z.object({ title: z.string(), date: z.string(), category: CommonModels.AchievementCategoryEnumSchema.nullable(), description: z.string().nullable(), issuer: z.string().nullable() });
export type AchievementOrAwardUpdateDto = z.infer<typeof AchievementOrAwardUpdateDtoSchema>;


/** 
 * LicenseOrCertificationUpdateDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } issuing_organization  
 * @property { string } issue_date  
 * @property { string } credential_id  
 * @property { string } verification_url  
 */
export const LicenseOrCertificationUpdateDtoSchema = z.object({ name: z.string(), issuing_organization: z.string().nullable(), issue_date: z.string().nullable(), credential_id: z.string().nullable(), verification_url: z.string().nullable() });
export type LicenseOrCertificationUpdateDto = z.infer<typeof LicenseOrCertificationUpdateDtoSchema>;


/** 
 * HobbyOrInterestUpdateDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { string } category  
 */
export const HobbyOrInterestUpdateDtoSchema = z.object({ name: z.string(), category: z.string() });
export type HobbyOrInterestUpdateDto = z.infer<typeof HobbyOrInterestUpdateDtoSchema>;


/** 
 * OthersUpdateDtoSchema 
 * @type { object }
 * @property { PublicationUpdateDto[] } publications  
 * @property { AchievementOrAwardUpdateDto[] } achievements_or_awards  
 * @property { LicenseOrCertificationUpdateDto[] } licenses_or_certifications  
 * @property { HobbyOrInterestUpdateDto[] } hobbies_or_interests  
 * @property { VolunteerExperienceUpdateDto[] } volunteer_experiences  
 */
export const OthersUpdateDtoSchema = z.object({ publications: z.array(CommonModels.PublicationUpdateDtoSchema).nullish(), achievements_or_awards: z.array(CommonModels.AchievementOrAwardUpdateDtoSchema), licenses_or_certifications: z.array(CommonModels.LicenseOrCertificationUpdateDtoSchema), hobbies_or_interests: z.array(CommonModels.HobbyOrInterestUpdateDtoSchema).nullish(), volunteer_experiences: z.array(CommonModels.VolunteerExperienceUpdateDtoSchema) });
export type OthersUpdateDto = z.infer<typeof OthersUpdateDtoSchema>;


/** 
 * ProfileV2DataUpdateDtoSchema 
 * @type { object }
 * @property { PersonUpdateDto } person  
 * @property { CompanyUpdateDto[] } companies  
 * @property { EducationUpdateDto[] } education  
 * @property { OthersUpdateDto } others  
 */
export const ProfileV2DataUpdateDtoSchema = z.object({ person: CommonModels.PersonUpdateDtoSchema, companies: z.array(CommonModels.CompanyUpdateDtoSchema), education: z.array(CommonModels.EducationUpdateDtoSchema), others: CommonModels.OthersUpdateDtoSchema });
export type ProfileV2DataUpdateDto = z.infer<typeof ProfileV2DataUpdateDtoSchema>;


/** 
 * MediaUploadThumbnailInstructionsSchema 
 * @type { object }
 * @property { string } id Media ID 
 * @property { string } method Method to upload with - PUT, POST, or custom instructions 
 * @property { string } url Url to upload to 
 * @property { array[] } fields Additional fields for the POST upload. Example: `key1,value1,key2,value2` 
 */
export const MediaUploadThumbnailInstructionsSchema = z.object({ id: z.string().nullable(), method: z.string().nullable(), url: z.string().nullable(), fields: z.array(z.array(z.string())).nullable() }).partial();
export type MediaUploadThumbnailInstructions = z.infer<typeof MediaUploadThumbnailInstructionsSchema>;


/** 
 * MediaUploadInstructionsSchema 
 * @type { object }
 * @property { string } id Media ID 
 * @property { string } method Method to upload with - PUT, POST, or custom instructions 
 * @property { string } url Url to upload to 
 * @property { array[] } fields Additional fields for the POST upload. Example: `key1,value1,key2,value2` 
 * @property { MediaUploadThumbnailInstructions } thumbnail Optional thumbnail upload instructions (for images with cropped thumbnails) 
 */
export const MediaUploadInstructionsSchema = z.object({ id: z.string().nullable(), method: z.string().nullable(), url: z.string().nullable(), fields: z.array(z.array(z.string())).nullable(), thumbnail: CommonModels.MediaUploadThumbnailInstructionsSchema.nullable() }).partial();
export type MediaUploadInstructions = z.infer<typeof MediaUploadInstructionsSchema>;


/** 
 * SkillRankSchema 
 * @type { object }
 * @property { string } skill  
 * @property { number } rank  
 */
export const SkillRankSchema = z.object({ skill: z.string(), rank: z.number() });
export type SkillRank = z.infer<typeof SkillRankSchema>;


/** 
 * ProfileJobSkillMatrixResponseDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } matrixCode  
 * @property { SkillRank[] } skillRanks  
 * @property { SkillRank[] } topSkills  
 */
export const ProfileJobSkillMatrixResponseDtoSchema = z.object({ title: z.string(), matrixCode: z.string(), skillRanks: z.array(CommonModels.SkillRankSchema), topSkills: z.array(CommonModels.SkillRankSchema) });
export type ProfileJobSkillMatrixResponseDto = z.infer<typeof ProfileJobSkillMatrixResponseDtoSchema>;


/** 
 * SkillScoreDtoSchema 
 * @type { object }
 * @property { string } name  
 * @property { number } profileScore  
 * @property { number } industryScore  
 */
export const SkillScoreDtoSchema = z.object({ name: z.string(), profileScore: z.number(), industryScore: z.number().nullish() });
export type SkillScoreDto = z.infer<typeof SkillScoreDtoSchema>;


/** 
 * ProfileBlsSkillIndustryResponseDtoSchema 
 * @type { object }
 * @property { string } status  
 * @property { SkillScoreDto[] } data  
 */
export const ProfileBlsSkillIndustryResponseDtoSchema = z.object({ status: CommonModels.StatusEnumSchema, data: z.array(CommonModels.SkillScoreDtoSchema) });
export type ProfileBlsSkillIndustryResponseDto = z.infer<typeof ProfileBlsSkillIndustryResponseDtoSchema>;


/** 
 * WorthNumbersDtoSchema 
 * @type { object }
 * @property { number } current Default: `0` 
 * @property { number } implied Default: `0` 
 * @property { number } historical Default: `0` 
 * @property { number } totalMiss Default: `0` 
 * @property { string } status  
 */
export const WorthNumbersDtoSchema = z.object({ current: z.number().nullable().default(0), implied: z.number().nullable().default(0), historical: z.number().nullable().default(0), totalMiss: z.number().nullable().default(0), status: CommonModels.StatusEnumSchema });
export type WorthNumbersDto = z.infer<typeof WorthNumbersDtoSchema>;


/** 
 * ProjectMediaDtoSchema 
 * @type { object }
 * @property { string } id Media ID. Example: `123e4567-e89b-12d3-a456-426614174000` 
 * @property { string } url URL to the original media file. Example: `https://example.com/image.jpg` 
 * @property { string } mimeType MIME type of the media. Example: `image/jpeg` 
 * @property { string } thumbnailUrl URL to the thumbnail (cropped image or auto-generated PDF preview). Example: `https://example.com/thumbnail.jpg` 
 */
export const ProjectMediaDtoSchema = z.object({ id: z.string(), url: z.string().nullable(), mimeType: z.string(), thumbnailUrl: z.string().nullish() });
export type ProjectMediaDto = z.infer<typeof ProjectMediaDtoSchema>;


/** 
 * ProjectWithMediaDtoSchema 
 * @type { object }
 * @property { string } id Id 
 * @property { string } createdAt Created at 
 * @property { string } updatedAt Updated at 
 * @property { string } title Title 
 * @property { string } description Description 
 * @property { boolean } featured Featured 
 * @property { ProjectMediaDto[] } media Default: `` 
 */
export const ProjectWithMediaDtoSchema = z.object({ id: z.string(), createdAt: z.iso.datetime({ offset: true }), updatedAt: z.iso.datetime({ offset: true }), title: z.string(), description: z.string(), featured: z.boolean(), media: z.array(CommonModels.ProjectMediaDtoSchema).default([]) });
export type ProjectWithMediaDto = z.infer<typeof ProjectWithMediaDtoSchema>;


/** 
 * SkillDiagramTopSkillsDtoSchema 
 * @type { object }
 * @property { string } skill  
 * @property { number } rank  
 */
export const SkillDiagramTopSkillsDtoSchema = z.object({ skill: z.string(), rank: z.number() });
export type SkillDiagramTopSkillsDto = z.infer<typeof SkillDiagramTopSkillsDtoSchema>;


/** 
 * ProfileAnalyticsSkillDiagramResponseDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } matrixCode  
 * @property { SkillDiagramTopSkillsDto[] } topSkills  
 */
export const ProfileAnalyticsSkillDiagramResponseDtoSchema = z.object({ title: z.string(), matrixCode: z.string().nullable(), topSkills: z.array(CommonModels.SkillDiagramTopSkillsDtoSchema).nullable() });
export type ProfileAnalyticsSkillDiagramResponseDto = z.infer<typeof ProfileAnalyticsSkillDiagramResponseDtoSchema>;


/** 
 * ProfessionalGrowthStrategySummaryDtoSchema 
 * @type { object }
 * @property { string } professional_name  
 * @property { string } current_role  
 * @property { string } core_narrative  
 * @property { string } strategic_focus  
 */
export const ProfessionalGrowthStrategySummaryDtoSchema = z.object({ professional_name: z.string(), current_role: z.string(), core_narrative: z.string(), strategic_focus: z.string() });
export type ProfessionalGrowthStrategySummaryDto = z.infer<typeof ProfessionalGrowthStrategySummaryDtoSchema>;


/** 
 * ProfessionalGrowthStrategyStrengthsDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } type  
 * @property { string } details  
 */
export const ProfessionalGrowthStrategyStrengthsDtoSchema = z.object({ title: z.string(), type: z.string(), details: z.string() });
export type ProfessionalGrowthStrategyStrengthsDto = z.infer<typeof ProfessionalGrowthStrategyStrengthsDtoSchema>;


/** 
 * ProfessionalGrowthStrategyPainPointsDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } type  
 * @property { string } details  
 * @property { string } update_section  
 */
export const ProfessionalGrowthStrategyPainPointsDtoSchema = z.object({ title: z.string(), type: z.string(), details: z.string(), update_section: z.string() });
export type ProfessionalGrowthStrategyPainPointsDto = z.infer<typeof ProfessionalGrowthStrategyPainPointsDtoSchema>;


/** 
 * ProfessionalGrowthStrategyPathsToSuccessDtoSchema 
 * @type { object }
 * @property { string } title  
 * @property { string } type  
 * @property { string } details  
 * @property { string } update_section  
 */
export const ProfessionalGrowthStrategyPathsToSuccessDtoSchema = z.object({ title: z.string(), type: z.string(), details: z.string(), update_section: z.string() });
export type ProfessionalGrowthStrategyPathsToSuccessDto = z.infer<typeof ProfessionalGrowthStrategyPathsToSuccessDtoSchema>;


/** 
 * ProfessionalGrowthStrategyDtoSchema 
 * @type { object }
 * @property { ProfessionalGrowthStrategySummaryDto } summary  
 * @property { ProfessionalGrowthStrategyStrengthsDto[] } strengths  
 * @property { ProfessionalGrowthStrategyPainPointsDto[] } pain_points  
 * @property { ProfessionalGrowthStrategyPathsToSuccessDto[] } paths_to_success  
 */
export const ProfessionalGrowthStrategyDtoSchema = z.object({ summary: CommonModels.ProfessionalGrowthStrategySummaryDtoSchema, strengths: z.array(CommonModels.ProfessionalGrowthStrategyStrengthsDtoSchema), pain_points: z.array(CommonModels.ProfessionalGrowthStrategyPainPointsDtoSchema), paths_to_success: z.array(CommonModels.ProfessionalGrowthStrategyPathsToSuccessDtoSchema) });
export type ProfessionalGrowthStrategyDto = z.infer<typeof ProfessionalGrowthStrategyDtoSchema>;


/** 
 * StrengthWeaknessAnalysisDtoSchema 
 * @type { object }
 * @property { ProfessionalGrowthStrategyDto } professional_growth_strategy  
 */
export const StrengthWeaknessAnalysisDtoSchema = z.object({ professional_growth_strategy: CommonModels.ProfessionalGrowthStrategyDtoSchema });
export type StrengthWeaknessAnalysisDto = z.infer<typeof StrengthWeaknessAnalysisDtoSchema>;


/** 
 * ProfileAnalyticsStrengthsWeaknessesResponseDtoSchema 
 * @type { object }
 * @property { StrengthWeaknessAnalysisDto } strengthWeaknessAnalysis  
 * @property { string } date  
 * @property { string } status  
 */
export const ProfileAnalyticsStrengthsWeaknessesResponseDtoSchema = z.object({ strengthWeaknessAnalysis: CommonModels.StrengthWeaknessAnalysisDtoSchema.nullable(), date: z.string().nullable(), status: CommonModels.StatusEnumSchema });
export type ProfileAnalyticsStrengthsWeaknessesResponseDto = z.infer<typeof ProfileAnalyticsStrengthsWeaknessesResponseDtoSchema>;


/** 
 * ActionPriorityMatrixItemV2DtoSchema 
 * @type { object }
 * @property { string } action  
 * @property { string } impact  
 * @property { number } increase  
 * @property { number } difficulty  
 * @property { string } why_missing  
 * @property { string } transformation  
 * @property { string } outcome  
 */
export const ActionPriorityMatrixItemV2DtoSchema = z.object({ action: z.string(), impact: z.string(), increase: z.number(), difficulty: z.number(), why_missing: z.string(), transformation: z.string(), outcome: z.string() });
export type ActionPriorityMatrixItemV2Dto = z.infer<typeof ActionPriorityMatrixItemV2DtoSchema>;


/** 
 * ActionPriorityMatrixV2DtoSchema 
 * @type { object }
 * @property { ActionPriorityMatrixItemV2Dto[] } education  
 * @property { ActionPriorityMatrixItemV2Dto[] } certification  
 * @property { ActionPriorityMatrixItemV2Dto[] } skills  
 * @property { ActionPriorityMatrixItemV2Dto[] } projects  
 * @property { ActionPriorityMatrixItemV2Dto[] } work_experience  
 */
export const ActionPriorityMatrixV2DtoSchema = z.object({ education: z.array(CommonModels.ActionPriorityMatrixItemV2DtoSchema), certification: z.array(CommonModels.ActionPriorityMatrixItemV2DtoSchema), skills: z.array(CommonModels.ActionPriorityMatrixItemV2DtoSchema), projects: z.array(CommonModels.ActionPriorityMatrixItemV2DtoSchema), work_experience: z.array(CommonModels.ActionPriorityMatrixItemV2DtoSchema) });
export type ActionPriorityMatrixV2Dto = z.infer<typeof ActionPriorityMatrixV2DtoSchema>;


/** 
 * ProfileAnalyticsActionPrioritiesV2ResponseDtoSchema 
 * @type { object }
 * @property { ActionPriorityMatrixV2Dto } actionPriorityMatrix  
 * @property { string } date  
 * @property { string } status  
 */
export const ProfileAnalyticsActionPrioritiesV2ResponseDtoSchema = z.object({ actionPriorityMatrix: CommonModels.ActionPriorityMatrixV2DtoSchema.nullable(), date: z.string().nullable(), status: CommonModels.StatusEnumSchema });
export type ProfileAnalyticsActionPrioritiesV2ResponseDto = z.infer<typeof ProfileAnalyticsActionPrioritiesV2ResponseDtoSchema>;


/** 
 * PaginationDtoSchema 
 * @type { object }
 * @property { string[] } items Items 
 * @property { object[] } order List of order definitions 
 * @property { number } page 1-indexed page number to begin from 
 * @property { string } cursor ID of item to start after 
 * @property { string } nextCursor Cursor for next set of items 
 * @property { number } limit Items per response 
 * @property { number } totalItems Total available items 
 */
export const PaginationDtoSchema = z.object({ items: z.array(z.string()), order: z.array(z.object({})).nullish(), page: z.number().nullish(), cursor: z.string().nullish(), nextCursor: z.string().nullish(), limit: z.number(), totalItems: z.number() });
export type PaginationDto = z.infer<typeof PaginationDtoSchema>;


/** 
 * LiveShowResponseDtoSchema 
 * @type { object }
 * @property { string } id  
 * @property { string } title  
 * @property { string } description  
 * @property { string } thumbnailUrl  
 * @property { string } provider  
 * @property { string } externalId  
 * @property { string } externalUrl  
 * @property { string } externalChatUrl  
 * @property { string } externalEmbedUrl  
 * @property { object } externalMetadata  
 * @property { string } scheduledStartAt  
 * @property { string } scheduledEndAt  
 * @property { string } actualStartAt  
 * @property { string } actualEndAt  
 * @property { string } publishedAt  
 * @property { string } category  
 * @property { string } status  
 * @property { string } createdAt  
 * @property { string } updatedAt  
 * @property { string } videoType  
 * @property { string } channelId  
 * @property { string } creationSource  
 */
export const LiveShowResponseDtoSchema = z.object({ id: z.string(), title: z.string(), description: z.string().nullish(), thumbnailUrl: z.string().nullish(), provider: z.string(), externalId: z.string(), externalUrl: z.string(), externalChatUrl: z.string().nullish(), externalEmbedUrl: z.string().nullish(), externalMetadata: z.object({}).catchall(z.any()).nullish(), scheduledStartAt: z.iso.datetime({ offset: true }).nullish(), scheduledEndAt: z.iso.datetime({ offset: true }).nullish(), actualStartAt: z.iso.datetime({ offset: true }).nullish(), actualEndAt: z.iso.datetime({ offset: true }).nullish(), publishedAt: z.iso.datetime({ offset: true }).nullish(), category: CommonModels.LiveShowCategoryEnumSchema, status: CommonModels.LiveShowStatusEnumSchema, createdAt: z.iso.datetime({ offset: true }), updatedAt: z.iso.datetime({ offset: true }), videoType: CommonModels.VideoTypeEnumSchema.nullish(), channelId: z.string().nullish(), creationSource: z.string().nullish() });
export type LiveShowResponseDto = z.infer<typeof LiveShowResponseDtoSchema>;


/** 
 * ProjectPaginationFilterDtoSchema 
 * @type { object }
 * @property { string[] } ids Ids 
 * @property { string } search Free text search 
 * @property { boolean } featured Is featured 
 */
export const ProjectPaginationFilterDtoSchema = z.object({ ids: z.array(z.string()).nullable(), search: z.string().nullable(), featured: z.boolean().nullable() }).partial();
export type ProjectPaginationFilterDto = z.infer<typeof ProjectPaginationFilterDtoSchema>;


/** 
 * ProjectDtoSchema 
 * @type { object }
 * @property { string } id Id 
 * @property { string } createdAt Created at 
 * @property { string } updatedAt Updated at 
 * @property { string } title Title 
 * @property { string } description Description 
 * @property { boolean } featured Featured 
 */
export const ProjectDtoSchema = z.object({ id: z.string(), createdAt: z.iso.datetime({ offset: true }), updatedAt: z.iso.datetime({ offset: true }), title: z.string(), description: z.string(), featured: z.boolean() });
export type ProjectDto = z.infer<typeof ProjectDtoSchema>;


/** 
 * EndorsementDtoSchema 
 * @type { object }
 * @property { string } id Unique identifier of the endorsement. Example: `550e8400-e29b-41d4-a716-446655440000` 
 * @property { string } userId User ID of the person being endorsed. Example: `550e8400-e29b-41d4-a716-446655440001` 
 * @property { string } userName  
 * @property { string } userProfilePictureUrl Profile picture URL of the person being endorsed. Example: `https://example.com/profile-pictures/user.jpg` 
 * @property { string } userEmail Email of the person being endorsed. Example: `user@example.com` 
 * @property { string } endorserId User ID of the person giving the endorsement. Example: `550e8400-e29b-41d4-a716-446655440002` 
 * @property { string } endorserName  
 * @property { string } endorserProfilePictureUrl Profile picture URL of the person giving the endorsement. Example: `https://example.com/profile-pictures/endorser.jpg` 
 * @property { string } endorserJobPosition Job position of the person giving the endorsement. Example: `Senior Software Engineer` 
 * @property { string } endorserEmail Email of the person giving the endorsement. Example: `endorser@example.com` 
 * @property { string } status Current status of the endorsement. Example: `APPROVED` 
 * @property { string } statusChangedAt When the status was last changed. Example: `2023-09-09T10:44:32.000Z` 
 * @property { string } relationship Relationship between endorser and endorsed. Example: `Colleague` 
 * @property { string } content Endorsement content/message. Example: `John is an excellent software developer with strong problem-solving skills.` 
 * @property { boolean } visible Whether the endorsement is visible. Example: `true` 
 * @property { string } createdAt When the endorsement was created. Example: `2023-09-09T10:44:32.000Z` 
 * @property { string } updatedAt When the endorsement was last updated. Example: `2023-09-09T10:44:32.000Z` 
 */
export const EndorsementDtoSchema = z.object({ id: z.string(), userId: z.string().nullish(), userName: z.string().nullish(), userProfilePictureUrl: z.string().nullish(), userEmail: z.string(), endorserId: z.string().nullish(), endorserName: z.string().nullish(), endorserProfilePictureUrl: z.string().nullish(), endorserJobPosition: z.string().nullish(), endorserEmail: z.string(), status: CommonModels.EndorsementStatusEnumSchema, statusChangedAt: z.iso.datetime({ offset: true }), relationship: z.string().nullish(), content: z.string().nullish(), visible: z.boolean(), createdAt: z.iso.datetime({ offset: true }), updatedAt: z.iso.datetime({ offset: true }) });
export type EndorsementDto = z.infer<typeof EndorsementDtoSchema>;


/** 
 * ProfileAnalyticsGradeResponseDtoSchema 
 * @type { object }
 * @property { number } score  
 * @property { string } status  
 * @property { string } date  
 * @property { string } lossDogGradeMessage  
 */
export const ProfileAnalyticsGradeResponseDtoSchema = z.object({ score: z.number().nullable(), status: CommonModels.StatusEnumSchema, date: z.string().nullable(), lossDogGradeMessage: z.string().nullish() });
export type ProfileAnalyticsGradeResponseDto = z.infer<typeof ProfileAnalyticsGradeResponseDtoSchema>;


/** 
 * ProfileAnalyticsCompletionResponseDtoSchema 
 * @type { object }
 * @property { number } profile  
 * @property { number } experience  
 * @property { number } skills  
 * @property { number } projects  
 */
export const ProfileAnalyticsCompletionResponseDtoSchema = z.object({ profile: z.number().nullable(), experience: z.number().nullable(), skills: z.number().nullable(), projects: z.number().nullable() });
export type ProfileAnalyticsCompletionResponseDto = z.infer<typeof ProfileAnalyticsCompletionResponseDtoSchema>;


}
